import { isSuccess } from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useProcessOcrInvoiceInferences } from 'ap-openapi';
import { useMemo } from 'react';

export const useOcrInvoiceInferences = ({
  file,
  invoiceId,
  is_used_ai_suggestion,
}: {
  file?: File;
  invoiceId?: string;
  is_used_ai_suggestion?: boolean;
} = {}) => {
  const { t } = useTranslation();

  const isPaymentRequestAiSuggestionFlag = useBooleanFlagValue(
    'payment-request-ai-suggestion',
    false
  );

  const requestBody = useMemo(
    () => ({
      file,
      invoice_id: invoiceId ?? undefined,
      is_used_ai_suggestion: is_used_ai_suggestion ?? undefined,
    }),
    [file, invoiceId, is_used_ai_suggestion]
  );

  const query = useProcessOcrInvoiceInferences(requestBody, {
    query: {
      enabled: !!file,
      staleTime: 0,
      gcTime: 0,
      retry: false,
    },
    request: {
      timeout: 60 * 1000,
      timeoutMessage: t('ocr_inference_timeout'),
      ...(isPaymentRequestAiSuggestionFlag && {
        headers: {
          agentApplicantResource: 'ocr_invoice_inference',
        },
      }),
    },
  });

  const apiError = useMemo(() => {
    if (query.data && !isSuccess(query.data.status)) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return query.data.data as Error;
    }
    return null;
  }, [query.data]);

  const error = apiError || query.error;

  return {
    data: apiError ? undefined : query.data?.data,
    error,
    isLoading: query.isLoading,
  };
};
